/* ============================================================
   custom.pdp.variant-select.js — the buy bar's variant dropdown
   31 Jul 2026

   WHAT IT IS FOR. Muddy Puppy Dog Soap has 100 variants in one option, one
   per dog breed. The chip grid is right for three sizes and absurd for a
   hundred breeds — it fills the page before the buy bar. Past a threshold
   (THRESHOLD below, 30) the chips are hidden by CSS and this puts a <select>
   in their place — same spot in the page, same drip pointer hanging off it
   into the buy bar. The bar itself is untouched: it still prints the variant
   name over the price, exactly as it does for a three-chip soap.

   IT DRIVES HORIZON'S OWN RADIOS, IT DOES NOT REPLACE THEM. On change it
   finds the radio carrying that value and clicks it. Everything downstream —
   price, image, the add-to-cart variant id, the URL — is then Horizon's own
   variant picker doing its normal job. Nothing here knows what a variant id
   is, so nothing here can put the wrong one in the cart.

   THE PICKER IS HIDDEN, NOT REMOVED, and that is load-bearing twice over: the
   radios are the thing being clicked, and they are also where this reads its
   option list from on every rebuild. So the select can never drift out of
   sync with the product — there is no second copy of the data.

   WHY A MutationObserver. Horizon re-renders the whole product form through
   the Section Rendering API on every variant change (see the long note in
   blocks/custom-product-copy.liquid about why the chip prices are generated
   CSS rather than injected spans). That wipes anything injected into the buy
   row, including this select — one tap and it would be gone. The observer
   rebuilds it the moment that happens. MutationObserver callbacks run as
   microtasks, before the next paint, so the swap is not visible.

   SINGLE-OPTION PRODUCTS ONLY (12 Aug, threshold 30 → 10 — Devin on the
   13-chip sampler: "it's just as bad as the tee. we could make a drop
   down like muddy puppy"). Two reasons, one structural, one hard:
   structurally, a multi-option product's rows are the tee's — wrapped
   chips, approved — and count can't tell the sampler's 13 from the tee's
   14 colours, but option count can. And hard: radiosIn() counts across
   the WHOLE picker, so the tee's 21 radios would cross any low threshold
   and this would build one menu mixing colours and sizes into the first
   fieldset. The multi-option bail in sync() is what makes a low
   threshold safe at all.

   ---- THE ONE THING TO KNOW IF THIS EVER LOOKS WRONG ----
   The threshold lives in TWO places by necessity: here, and as
   :nth-of-type(10) in the CSS that hides the chips (custom.pdp.buybar.css
   §10a) — BOTH gated to single-option pickers. CSS cannot count elements,
   only test for the presence of an nth one, and JS cannot read that rule
   back. Change one, change the other, or you get a product with both a
   chip grid and a dropdown, or neither.
   ============================================================ */
(function () {
  'use strict';

  var THRESHOLD = 10;
  var SELECT_CLASS = 'ffg-varselect';
  var WRAP_CLASS = 'ffg-varselect-wrap';

  function radiosIn(picker) {
    return picker.querySelectorAll('.variant-option__button-label input[type="radio"]');
  }

  function labelFor(input) {
    var label = input.closest('.variant-option__button-label');
    var text = label && label.querySelector('.variant-option__button-label__text');
    /* The chip relabel map (Small -> Small Bar) is painted by generated CSS on
       a ::after, which textContent cannot see. The radio's own value is the
       real option value and the only thing guaranteed to be here. */
    return (text && text.textContent.trim()) || input.value;
  }

  /* RE-READ EVERY RENDER, NOT ONCE AT LOAD (22 Aug — Devin: sampler dropdown
     shows no prices when you NAVIGATE to it). This file is a global module
     (custom.globals.liquid), so it initialises on whatever page the visit
     starts on. Read once, the map is null on any non-dropdown landing page
     and stays null through every no-reload swap after it — so the samplers /
     Muddy Puppy dropdown swapped in later renders unpriced. It only worked on
     a DIRECT load of the product (how batch 221 was verified). The fresh
     `[data-ffg-variant-prices]` rides in the swapped #MainContent, so refresh
     it at the top of each sync pass. */
  var priceMap = null;
  function refreshPriceMap() {
    var el = document.querySelector('[data-ffg-variant-prices]');
    if (!el) {
      priceMap = null;
      return;
    }
    try {
      priceMap = JSON.parse(el.textContent);
    } catch (e) {
      priceMap = null;
    }
  }

  /* THE 24 LEADS (21 Aug, Devin: "make the 24 flavor sampler be the
     default when you arrive at the page"). Arrival only — a shared
     ?variant= link keeps its own selection. One-shot. */
  var ffgDefaulted = false;
  function applySamplerDefault(picker) {
    if (ffgDefaulted) return;
    if (window.location.pathname.indexOf('/products/samplers') === -1) return;
    if (window.location.search.indexOf('variant=') !== -1) { ffgDefaulted = true; return; }
    var inputs = radiosIn(picker);
    Array.prototype.some.call(inputs, function (input) {
      if (input.value.indexOf('24') !== -1) {
        ffgDefaulted = true;
        if (!input.checked) input.click();
        return true;
      }
      return false;
    });
  }

  function build(picker) {
    var inputs = radiosIn(picker);
    var select = document.createElement('select');
    select.className = SELECT_CLASS;

    var optionName = inputs[0] && inputs[0].getAttribute('data-option-name');
    select.setAttribute('aria-label', optionName || 'Choose an option');

    Array.prototype.forEach.call(inputs, function (input) {
      var option = document.createElement('option');
      option.value = input.value;
      var soldOut = input.getAttribute('data-option-available') === 'false';
      /* Price rides the label (21 Aug, Devin: "the prices should show on
         the drop down menu") — from the Liquid-rendered map; a value
         missing from the map (or no map at all) just renders unpriced. */
      var price = priceMap && priceMap[input.value];
      option.textContent =
        labelFor(input) + (price ? ' — ' + price : '') + (soldOut ? ' — sold out' : '');
      /* Sold-out variants stay SELECTABLE on purpose: that is Horizon's own
         behaviour for the chips (they render struck through, not disabled),
         and picking one is how a shopper sees it is unavailable rather than
         wondering why it is missing from the list. */
      if (input.checked) option.selected = true;
      select.appendChild(option);
    });

    select.addEventListener('change', function () {
      var target = null;
      Array.prototype.some.call(radiosIn(picker), function (input) {
        if (input.value === select.value) {
          target = input;
          return true;
        }
        return false;
      });
      if (target && !target.checked) target.click();
    });

    return select;
  }

  function sync(scope) {
    var picker = scope.querySelector('.variant-picker');
    if (!picker) return;
    applySamplerDefault(picker);

    /* The menu goes WHERE THE CHIPS WERE — inside the option's own fieldset,
       under its label — not into the buy bar. It is the variant control, so it
       belongs in the variant control's place, and that is also what lets the
       drip pointer hang off it into the bar exactly as it does off a chip. */
    var host = picker.querySelector('.variant-option--buttons') || picker;
    var inputs = radiosIn(picker);
    var existing = host.querySelector('.' + SELECT_CLASS);

    /* Multi-option products keep their chips (see the header note): their
       rows wrap, and radiosIn counts across every option — a combined
       menu here would be colours and sizes shuffled into one list. */
    var multiOption = picker.querySelectorAll('.variant-option').length > 1;

    if (multiOption || inputs.length < THRESHOLD) {
      /* Below the threshold the chips are the picker. If a select is somehow
         left over — a section re-render that changed the option count — take
         it out rather than show both. */
      if (existing) {
        var staleWrap = existing.closest('.' + WRAP_CLASS);
        (staleWrap || existing).remove();
      }
      return;
    }

    if (existing) {
      /* Still ours and still in place: just re-point it at the current
         variant, which is all a re-render can have changed. */
      var checked = picker.querySelector(
        '.variant-option__button-label input[type="radio"]:checked'
      );
      if (checked && existing.value !== checked.value) existing.value = checked.value;
      return;
    }

    /* The wrapper exists for ONE reason: a <select> cannot carry ::before or
       ::after in any engine, and the drip pointer is a pseudo-element. The
       wrapper is what the pointer hangs off. */
    var wrap = document.createElement('div');
    wrap.className = WRAP_CLASS;
    wrap.appendChild(build(picker));
    host.appendChild(wrap);
  }

  function syncAll() {
    refreshPriceMap();
    Array.prototype.forEach.call(document.querySelectorAll('.product-details'), sync);
  }

  /* WATCH THE SECTION WRAPPER, NOT .product-details — same choice, same reason
     as assets/ffg-buybar-total.js: a variant tap can replace the details
     subtree wholesale, and an observer attached to the element that got
     replaced is left watching a detached node and never fires again. The
     section wrapper outlives every re-render.

     Unlike that script this one DOES write into the tree it watches, so the
     insert re-triggers the callback. It cannot loop: the second pass finds the
     select already in the bar and returns without touching anything. */
  var observer = null;

  function observe() {
    if (!window.MutationObserver) return;
    var scope = document.querySelector('[id^="ProductInformation-"]');
    var host = (scope && scope.closest('[id^="shopify-section-"]')) || document.body;
    /* init() runs again per navigation now — disconnect the previous observer
       (usually left holding a section wrapper that a swap detached) rather
       than stacking a new one on top each time. */
    if (observer) observer.disconnect();
    observer = new MutationObserver(syncAll);
    observer.observe(host, { childList: true, subtree: true });
  }

  function init() {
    observe();
    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders sections wholesale. */
  document.addEventListener('shopify:section:load', init);

  /* This file loads on EVERY page since 10 Aug (see custom.globals.liquid), and
     no-reload navigation swaps product markup in without a new document — so
     the section wrapper the observer holds is replaced under it on every
     navigation. Re-arm exactly as for the editor's section re-render. */
  document.addEventListener('ffg:navigated', init);
})();
